import { useRef, useState, type CompositionEvent, type FormEvent } from 'react';

interface SearchFormProps {
  onSubmit: (name: string, region: string) => void;
  loading: boolean;
}

/**
 * UI-1.1｜搜尋輸入正規化（僅處理「輸入法 / 空白 / Unicode 表示差異」，不改動可見店名內容）
 *
 * - 移除零寬／不可見格式字元（中文輸入法在部分行動裝置會殘留 U+200B~U+200D / U+2060 / U+FEFF）
 * - 全形空白（U+3000）與不斷行空白（U+00A0）轉為一般空白
 * - Unicode NFC 正規化（同一字元的組合／分解表示差異）
 * - 去除前後空白
 *
 * 不刪除可見中文字、不做模糊轉換、不自行補城市、不猜地址。
 */
export function normalizeSearchInput(raw: string): string {
  if (!raw) return '';
  return raw
    .replace(/[\u200B-\u200D\u2060\uFEFF]/g, '')
    .replace(/[\u00A0\u3000]/g, ' ')
    .normalize('NFC')
    .trim();
}

export default function SearchForm({ onSubmit, loading }: SearchFormProps) {
  const [name, setName] = useState('');
  const [region, setRegion] = useState('');
  const [error, setError] = useState('');

  // UI-1.1｜以 DOM 實際輸入值為送出依據。
  // iOS Safari + 中文輸入法的 composition 期間，React 受控 state 可能落後於畫面所見內容；
  // 送出時直接讀取 input 元素當下的 value，可避免送出 stale / 殘缺字串
  //（這正是同一店名 Desktop 成功、Mobile 失敗的最可能前端差異來源）。
  const nameRef = useRef<HTMLInputElement>(null);
  const regionRef = useRef<HTMLInputElement>(null);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (loading) return;

    // 以 DOM 當下值為準，退回 React state 作保險。
    const rawName = nameRef.current?.value ?? name;
    const rawRegion = regionRef.current?.value ?? region;

    const trimmedName = normalizeSearchInput(rawName);
    const normalizedRegion = normalizeSearchInput(rawRegion);

    if (trimmedName.length < 2) {
      setError('請輸入至少 2 個字的店家名稱。');
      return;
    }
    setError('');
    // 送出後回寫 state，維持受控輸入顯示一致（不影響已送出的查詢）。
    setName(trimmedName);
    setRegion(normalizedRegion);
    onSubmit(trimmedName, normalizedRegion);
  };

  // 輸入法 composition 結束時強制同步最終值，避免 state 停在組字前。
  const handleNameCompositionEnd = (event: CompositionEvent<HTMLInputElement>) => {
    setName(event.currentTarget.value);
  };
  const handleRegionCompositionEnd = (event: CompositionEvent<HTMLInputElement>) => {
    setRegion(event.currentTarget.value);
  };

  return (
    <section className="ds-container relative z-10">
      <div className="mx-auto -mt-10 w-full max-w-search rounded-2xl border border-background-300 bg-background-50 p-5 shadow-card md:-mt-12 md:p-8">
        <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-5">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div className="flex flex-col gap-2">
              <label htmlFor="store-name" className="text-base font-semibold text-foreground-800">
                店家名稱 <span className="text-rose-500">*</span>
              </label>
              <div className="flex h-14 items-center gap-3 rounded-xl border border-background-400 bg-background-50 px-4 transition-colors focus-within:border-primary-500">
                <i className="ri-store-2-line text-xl text-foreground-400" />
                <input
                  id="store-name"
                  name="storeName"
                  type="text"
                  ref={nameRef}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onCompositionEnd={handleNameCompositionEnd}
                  placeholder="例如：天地人網"
                  disabled={loading}
                  className="w-full bg-transparent text-base text-foreground-950 placeholder:text-foreground-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
                />
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="store-region" className="text-base font-semibold text-foreground-800">
                城市／行政區 <span className="text-sm font-normal text-foreground-500">（建議填寫）</span>
              </label>
              <div className="flex h-14 items-center gap-3 rounded-xl border border-background-400 bg-background-50 px-4 transition-colors focus-within:border-primary-500">
                <i className="ri-map-pin-line text-xl text-foreground-400" />
                <input
                  id="store-region"
                  name="region"
                  type="text"
                  ref={regionRef}
                  value={region}
                  onChange={(e) => setRegion(e.target.value)}
                  onCompositionEnd={handleRegionCompositionEnd}
                  placeholder="例如：台中市西屯區"
                  disabled={loading}
                  className="w-full bg-transparent text-base text-foreground-950 placeholder:text-foreground-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
                />
              </div>
            </div>
          </div>

          {error && (
            <p className="flex items-center gap-2 text-base text-rose-600">
              <i className="ri-error-warning-line text-lg" />
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="inline-flex h-14 w-full cursor-pointer items-center justify-center gap-2.5 whitespace-nowrap rounded-xl bg-primary-700 px-8 text-base font-semibold text-background-50 transition-colors hover:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto sm:min-w-[220px] sm:self-end"
          >
            {loading ? (
              <>
                <i className="ri-loader-4-line animate-spin text-lg" />
                查詢中…
              </>
            ) : (
              <>
                <i className="ri-search-line text-lg" />
                開始免費健診
              </>
            )}
          </button>
        </form>
      </div>
    </section>
  );
}